import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsPage = () => {
  const navigate = useNavigate();

  const termsContent = `Terms of Service
ClubHudl – Cricket Club Management Platform
Last Updated: January 4, 2026

These Terms of Service ("Terms") govern your access to and use of the ClubHudl application, website, APIs, and related services ("Service"), operated by ClubHudl LLC ("Company," "we," "us," or "our"). By creating an account, accessing, or using the Service, you agree to be bound by these Terms.

If you do not agree, you may not use the Service.

1. Eligibility
You must be at least 13 years old (or the minimum age required in your jurisdiction) to use the Service. By using the Service, you represent that you meet all eligibility requirements.

2. Your Account
You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use.

3. User Data and Permissions

3.1 Data You Provide
By using the Service, you provide or generate data including profile information, club membership details, communications, uploaded content, and activity logs ("User Data").

You grant ClubHudl LLC a worldwide, non‑exclusive, royalty‑free license to collect, store, process, use, modify, display, and distribute User Data solely for the purpose of operating, improving, and providing the Service, including:
• Managing clubs you create or join
• Enabling communication and coordination within clubs
• Supporting administrative workflows
• Maintaining platform security and performance

3.2 Role‑Based Access
By joining or creating a club, you acknowledge and agree that:
• Club administrators, and
• Any roles or permissions defined by that club
may access your User Data as needed to manage and operate the club. This may include viewing your profile, attendance, payments, performance data, or any other information relevant to club operations.

We are not responsible for how club administrators or other users with elevated permissions choose to use or manage your data within their clubs.

3.3 Use of Data for Advertising
You agree that ClubHudl LLC may use your User Data to:
• Personalize your experience
• Provide recommendations
• Serve you advertisements or promotional content in the future

We will not sell your personal data to third parties without your explicit consent.

4. Acceptable Use
You agree not to:
• Use the Service for unlawful, harmful, or fraudulent purposes
• Upload content that infringes on intellectual property or privacy rights
• Attempt to access data or systems you are not authorized to access
• Interfere with the operation or security of the Service

We may suspend or terminate your account for violations.

5. Club Content and Responsibilities
Clubs created on the platform are independently managed by their administrators. ClubHudl LLC is not responsible for:
• Accuracy of club information
• Decisions made by club administrators
• Actions taken by club members
• Any disputes between users or clubs

You acknowledge that clubs operate autonomously and that your participation is voluntary.

6. No Liability for User or Club Data
You agree that ClubHudl LLC:
• Is not liable for any content, data, or information submitted, processed, or displayed through the Service
• Does not control how clubs or administrators use your data
• Is not responsible for errors, omissions, or misuse of data by any user, club, or third‑party integration

Your use of the Service is at your own risk.

7. Third‑Party Services and Integrations
The Service may integrate with third‑party tools, payment processors, or communication platforms. We are not responsible for the actions, policies, or data practices of these third parties.

8. Intellectual Property
All trademarks, logos, software, and content provided by ClubHudl LLC remain our exclusive property. You may not copy, modify, distribute, or reverse‑engineer any part of the Service.

9. Termination
We may suspend or terminate your access at any time for violations of these Terms or for any reason at our discretion. You may stop using the Service at any time.

10. Disclaimer of Warranties
The Service is provided "as is" and "as available" without warranties of any kind, express or implied. We do not guarantee uninterrupted or error‑free operation.

11. Limitation of Liability
To the fullest extent permitted by law:
• ClubHudl LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages
• Our total liability for any claim shall not exceed the amount you paid us in the past 12 months (or $0 if you have not paid anything)

You agree that this limitation is a fundamental part of the agreement.

12. Indemnification
You agree to indemnify and hold harmless ClubHudl LLC, its officers, employees, and partners from any claims arising from:
• Your use of the Service
• Your violation of these Terms
• Actions taken by clubs or administrators related to your data

13. Changes to These Terms
We may update these Terms at any time. Continued use of the Service after changes become effective constitutes acceptance of the revised Terms.

14. Governing Law
These Terms are governed by the laws of the state in which ClubHudl LLC is registered, without regard to conflict‑of‑law principles.

15. Contact
For questions about these Terms, contact us at:
contact-us@clubhudl.com`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={() => {
              // Try to close the tab if opened in new tab, otherwise go home
              if (window.opener) {
                window.close();
              } else {
                navigate('/');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-8">
            <div className="flex items-center gap-4 mb-2">
              <FileText size={32} />
              <h1 className="text-3xl font-bold">Terms of Service</h1>
            </div>
            <p className="text-blue-100">Version 1.0 - Effective January 4, 2026</p>
          </div>

          {/* Terms Content */}
          <div className="p-8">
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {termsContent}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;